import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuButtonOption} from '@Components/contextual-menu/components/contextual-menu-button-option';
import {ContextualButtonItemType} from '@Components/contextual-menu/components/button-items';
import {CIRCLE_BRUSH, ERASER_BRUSH, PENCIL_BRUSH, SPRAY_BRUSH} from '@PosterWhiteboard/models/draw.class';
import {ContextualMenuDivider} from '@Components/contextual-menu/components/contextual-menu-divider';
import pencil from '@Assets/postermaker/draw-svgs/pencil.svg';
import circle from '@Assets/postermaker/draw-svgs/circle.svg';
import spray from '@Assets/postermaker/draw-brush-spray-stroke.png';
import eraser from '@Assets/postermaker/draw-svgs/eraser.svg';
import {useAppSelector} from '@/hooks';

export function DrawBrushOptions(): ReactElement {
  const drawObject = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing;
  });
  const isImageGeneratingFromDrawing = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isImageBeingGenerated;
  });

  if (!drawObject) {
    throw new Error('Draw Object is undefined');
  }

  const getBrushNameForId = (id: string): string => {
    switch (id) {
      case 'draw-eraser-btn':
        return ERASER_BRUSH;
      case 'draw-pencil-btn':
        return PENCIL_BRUSH;
      case 'draw-circle-btn':
        return CIRCLE_BRUSH;
      case 'draw-spray-btn':
        return SPRAY_BRUSH;
      default:
        return PENCIL_BRUSH;
    }
  };

  const getBrushToApplyFromSelectedBrush = (id: string): string => {
    if (id === window.posterEditor?.whiteboard?.drawing.drawBrush.brush && id === ERASER_BRUSH) {
      return PENCIL_BRUSH;
    }

    return id;
  };

  const updateBrushType = (id: string): void => {
    void window.posterEditor?.whiteboard?.drawing.updateBrushType(getBrushToApplyFromSelectedBrush(getBrushNameForId(id)));
  };

  const getDrawEraserOption = (): ReactElement => {
    return (
      <ContextualMenuButtonOption
        id="draw-eraser-mode"
        data={[
          {
            id: 'draw-eraser-btn',
            type: ContextualButtonItemType.SVG_BUTTON,
            text: window.i18next.t('pmwjs_eraser'),
            svgPath: eraser as string,
            onClick: (id: string): void => {
              updateBrushType(id);
            },
            isSelected: drawObject?.drawBrush?.brush === ERASER_BRUSH,
            isDisabled: isImageGeneratingFromDrawing,
          },
        ]}
      />
    );
  };

  const getDrawButtons = (): ReactElement => {
    return (
      <ContextualMenuButtonOption
        id="inline-draw-button-option"
        data={[
          {
            id: 'draw-pencil-btn',
            type: ContextualButtonItemType.SVG_BUTTON,
            text: window.i18next.t('pmwjs_pencil'),
            svgPath: pencil as string,
            onClick: (id: string): void => {
              updateBrushType(id);
            },
            isSelected: drawObject?.drawBrush?.brush === PENCIL_BRUSH,
            isDisabled: isImageGeneratingFromDrawing,
          },
          {
            id: 'draw-circle-btn',
            type: ContextualButtonItemType.SVG_BUTTON,
            text: window.i18next.t('pmwjs_circle'),
            svgPath: circle as string,
            onClick: (id: string): void => {
              updateBrushType(id);
            },
            isSelected: drawObject?.drawBrush?.brush === CIRCLE_BRUSH,
            isDisabled: isImageGeneratingFromDrawing,
          },
          {
            id: 'draw-spray-btn',
            type: ContextualButtonItemType.SVG_BUTTON,
            text: window.i18next.t('pmwjs_spray'),
            svgPath: spray as string,
            onClick: (id: string): void => {
              updateBrushType(id);
            },
            isSelected: drawObject?.drawBrush?.brush === SPRAY_BRUSH,
            isDisabled: isImageGeneratingFromDrawing,
          },
        ]}
      />
    );
  };

  return (
    <>
      {getDrawEraserOption()}
      {getDrawButtons()}
      <ContextualMenuDivider id="draw-divider" className="spacing-m-b-2" />
    </>
  );
}
